import React, { FormEvent, ReactNode } from 'react'
import styled from '@emotion/styled'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { f2Grey } from '../colors'
import HeaderText from './HeaderText'
import { theme } from '../mui'

export enum FormModalAction {
  EDIT = 'Edit',
  CREATE = 'Create',
}

export const formActionVerb = (action: FormModalAction) => {
  return action === FormModalAction.CREATE ? 'created' : 'edited'
}

interface MediaPlanAdvisorFormModalProps {
  actionsDisabled: boolean
  children: ReactNode | ReactNode[]
  open: boolean
  close: () => void
  onSubmit: ((event: FormEvent<HTMLFormElement>) => void) | undefined
  primaryActionText?: string
  title: string
  insights?: readonly { readonly text: string; readonly id: string }[]
}

export const S = {
  IconButton: styled(IconButton)`
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
  `,
  DialogBody: styled.div`
    display: flex;
    flex-direction: row;
    margin: 0 1.5rem 0.5rem 1.5rem;
  `,
  Form: styled.form`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `,
  FormContent: styled.div`
    display: flex;
    flex-direction: column;
    margin: 1rem 0.5rem 1rem 0;
    height: max-content;
    & > * {
      width: 25rem;
    }
    & > :not(:last-child) {
      margin-bottom: 1rem;
    }
  `,
  HeaderText: styled(HeaderText)`
    margin-bottom: 1rem;
  `,
  MediaPlanAdvisor: styled.div`
    width: 15rem;
    margin: 0 0 3.25rem 1rem;
    display: flex;
    align-items: center;
    flex-direction: column;
  `,
  MediaPlanAdvisorInsights: styled.div`
    overflow: auto;
    max-height: calc(100vh - 12rem);
    padding-right: 0.25rem;
  `,
  MediaPlanAdvisorInsight: styled.div`
    background: ${f2Grey};
    border-radius: 0.25rem;
    padding: 1rem;
    color: ${theme.palette.text.disabled};
    font-size: 0.875rem;
    width: calc(100% - 2rem);
    margin: 0.5rem 0;
  `,
}

const FormModal = ({
  actionsDisabled,
  children,
  open,
  close,
  onSubmit,
  primaryActionText = 'Save',
  title,
  insights,
}: MediaPlanAdvisorFormModalProps) => {
  return (
    <>
      <Dialog open={open} onClose={close}>
        <DialogTitle>
          {title}
          <S.IconButton
            aria-label="close"
            onClick={close}
            disabled={actionsDisabled}
          >
            <CloseIcon />
          </S.IconButton>
        </DialogTitle>
        <S.DialogBody>
          <S.Form onSubmit={onSubmit}>
            <S.FormContent>{children}</S.FormContent>
            <DialogActions>
              <Button disabled={actionsDisabled} onClick={close}>
                Cancel
              </Button>
              <Button
                disabled={actionsDisabled}
                type="submit"
                variant="contained"
              >
                {primaryActionText}
              </Button>
            </DialogActions>
          </S.Form>
          {insights != null && insights.length > 0 && (
            <S.MediaPlanAdvisor>
              <S.HeaderText>Media Plan Advisor</S.HeaderText>
              <S.MediaPlanAdvisorInsights>
                {insights.map((it) => (
                  <S.MediaPlanAdvisorInsight key={it.id}>
                    {it.text}
                  </S.MediaPlanAdvisorInsight>
                ))}
              </S.MediaPlanAdvisorInsights>
            </S.MediaPlanAdvisor>
          )}
        </S.DialogBody>
      </Dialog>
    </>
  )
}

export default FormModal
